import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ShowCard from '@/components/feature/ShowCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getShowById, getRelatedShows, categories } from '@/mocks/shows';
import NotFound from '@/pages/NotFound';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const show = getShowById(id || '');

  if (!show) {
    return <NotFound />;
  }

  const relatedShows = getRelatedShows(show.id, show.category, 4);
  const category = categories.find((c) => c.slug === show.category);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: show.title,
    description: show.description,
    datePublished: show.date,
    author: {
      '@type': 'Person',
      name: show.host,
    },
    publisher: {
      '@type': 'Organization',
      name: '天天影视',
      url: 'https://tiantian.tv',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://tiantian.tv/article/${show.id}`,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${show.title}在哪里可以观看？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `您可以在天天影视平台在线观看《${show.title}》，支持高清流畅播放。${show.updateDay}更新。`,
        },
      },
      {
        '@type': 'Question',
        name: `${show.title}的主持人和嘉宾是谁？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `《${show.title}》由${show.host}主持，嘉宾包括${show.guests.join('、')}等。`,
        },
      },
      {
        '@type': 'Question',
        name: `${show.title}每期多长时间？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `《${show.title}》每期时长约${show.duration}，共${show.episodes}期。`,
        },
      },
    ],
  };

  const videoSchema = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: show.title,
    description: show.description,
    thumbnailUrl: show.image,
    uploadDate: show.date,
    duration: show.duration,
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(videoSchema) }}
      />
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />

        <main className="flex-1">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category?.name || '综艺', href: `/category/${show.category}` },
              { label: show.title },
            ]}
          />

          <article className="w-full px-4 md:px-6 pb-10">
            <div className="max-w-[1024px] mx-auto">
              <div className="mb-8">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {show.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
                  {show.title}
                </h1>
                <p className="text-sm md:text-base text-foreground-600 mb-4">{show.description}</p>
                <div className="flex flex-wrap items-center gap-4 text-xs md:text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i> 主持：{show.host}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i> {show.views}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-secondary-500"></i> {show.rating}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i> {show.date}
                  </span>
                </div>
              </div>

              <div className="relative rounded-xl overflow-hidden mb-8 aspect-video bg-background-900">
                <img
                  src={show.image}
                  alt={show.title}
                  title={`${show.title} - 天天影视综艺节目在线观看`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center group cursor-pointer">
                  <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 hover:bg-primary-500 text-white transition-all hover:scale-110">
                    <i className="ri-play-fill text-2xl md:text-3xl ml-1"></i>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                  <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
                    <div
                      className="prose prose-sm md:prose-base max-w-none text-foreground-800"
                      dangerouslySetInnerHTML={{ __html: show.fullContent }}
                    />
                  </div>
                </div>

                <aside className="space-y-6">
                  <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                    <h3 className="text-sm font-medium text-foreground-900 mb-4 flex items-center gap-2">
                      <i className="ri-information-line text-primary-500"></i> 节目信息
                    </h3>
                    <dl className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-foreground-500">主持人</dt>
                        <dd className="text-foreground-800 font-medium">{show.host}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-foreground-500">嘉宾阵容</dt>
                        <dd className="text-foreground-800 text-right">{show.guests.join('、')}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-foreground-500">总期数</dt>
                        <dd className="text-foreground-800">{show.episodes}期</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-foreground-500">每期时长</dt>
                        <dd className="text-foreground-800">{show.duration}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-foreground-500">更新频率</dt>
                        <dd className="text-foreground-800">{show.updateDay}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-foreground-500">播放平台</dt>
                        <dd className="text-foreground-800">{show.platform}</dd>
                      </div>
                    </dl>
                  </div>

                  {relatedShows.length > 0 && (
                    <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                      <h3 className="text-sm font-medium text-foreground-900 mb-4 flex items-center gap-2">
                        <i className="ri-play-list-2-line text-primary-500"></i> 相关推荐
                      </h3>
                      <div className="space-y-3">
                        {relatedShows.map((s) => (
                          <Link
                            key={s.id}
                            to={`/article/${s.id}`}
                            className="flex gap-3 group cursor-pointer"
                          >
                            <div className="w-20 h-12 rounded-md overflow-hidden flex-shrink-0">
                              <img
                                src={s.image}
                                alt={s.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1">
                                {s.title}
                              </p>
                              <p className="text-[10px] text-foreground-500 mt-0.5">
                                <i className="ri-star-fill text-secondary-500 text-[10px]"></i> {s.rating}
                                <span className="mx-1">·</span>
                                {s.views}
                              </p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="bg-gradient-to-br from-primary-50 to-accent-50 border border-primary-100 rounded-lg p-5 text-center">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-3">
                      <i className="ri-notification-3-line text-lg"></i>
                    </div>
                    <h4 className="text-sm font-medium text-foreground-900 mb-1">追综提醒</h4>
                    <p className="text-xs text-foreground-500 mb-3">不想错过精彩综艺？开启更新提醒！</p>
                    <button className="whitespace-nowrap px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-xs rounded-full cursor-pointer transition-colors">
                      订阅更新通知
                    </button>
                  </div>
                </aside>
              </div>

              {relatedShows.length > 0 && (
                <section className="mt-12">
                  <h2 className="text-lg font-heading text-foreground-900 mb-5">相关推荐</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {relatedShows.map((s) => (
                      <ShowCard key={s.id} show={s} />
                    ))}
                  </div>
                </section>
              )}
            </div>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}